import { deriveAlertLink, deriveOriginLink } from '~support-at-home/lib/derive';

import { faEdit } from '~components/icon';
import { Column, Row } from '~components/table-view';
import { linkCellRender } from '~components/table-view/columnRenderers';
import ClaimItemActions from '~support-at-home/components/claims/ClaimItemActions';
import ClaimSahPushIcon from '~support-at-home/components/claims/ClaimSahPushIcon';
import ClaimValidIcon from '~support-at-home/components/claims/ClaimValidIcon';

import { SahService, SahServiceItem, SahServiceWrap } from '~lookups/models';

import BudgetIcon from '~support-at-home/forms/supplier-invoices/BudgetIcon';

import { ServiceTypeRow } from '~appointments/reducers/serviceTypes';

import ServiceDetail from '../../components/suppliers/supplier-invoices/ServiceDetail';

export const claimItemColumnsFactory = (
  sahServices: SahService[],
  serviceTypes: ServiceTypeRow[],
  sahServiceWraps: SahServiceWrap[],
  sahServiceItems: SahServiceItem[],
  onIgnore?: (id: string) => void,
  isValidating?: boolean,
  // TAV-2590: whether this claim has a Services Australia invoice (`claim.sa_invoice_id` set).
  // Trailing/optional so every existing caller keeps compiling untouched.
  hasApiPush?: boolean,
  // TAV-2592: hides the validation Error/Investigate columns once the claim is finalised. Finalise
  // is disabled while items_with_errors > 0 (Claim.tsx), so a finalised claim has no validation
  // errors by construction and both columns are dead width from that point on.
  isFinalised?: boolean,
): Column[] => {
  const columns: Column[] = [
    {
      id: 'reference',
      header: 'Ref.',
      type: 'text',
      isEditable: false,
    },
    {
      id: 'origin_tag',
      header: 'Origin',
      type: 'text',
      renderReadOnly: linkCellRender(
        (row) => window.open(deriveOriginLink(row), '_blank', 'noopener,noreferrer'),
        faEdit,
        {
          classNames: {
            cell: 'px-1',
          },
        } as any,
      ),
      isEditable: false,
    },
    {
      id: 'client_id',
      header: 'Client Name',
      type: 'text',
      deriveValue: (row) => row.client?.full_name || '',
      isEditable: false,
    },
    {
      id: 'service_date',
      header: 'Date',
      type: 'date',
      isEditable: false,
    },
    {
      id: 'sah_service_id',
      header: 'SaH Service',
      type: 'text',
      deriveValue: (row) => {
        const service = sahServices.find((s) => s.id === row.sah_service_id);
        return service ? service.name : '';
      },
      isEditable: false,
    },
    {
      id: 'service_details',
      header: '',
      type: 'custom',
      style: { width: '50px' },
      classNames: {
        header: 'cell-align-center',
        column: 'cell-align-center',
      },
      component: ServiceDetail,
      componentProps: (row: Row) => ({
        row,
        wraps: sahServiceWraps.filter((wrap) => wrap.service_id === row.sah_service_id) ?? [],
        items: sahServiceItems.filter((item) => item.service_id === row.sah_service_id) ?? [],
        readOnly: row.statement?.id !== undefined,
      }),
      onChange: (row: Row) => {
        // Extract fields directly from service-details and return as row-level fields
        const details = row['service_details']?.details || {};
        return {
          sah_wrap_id: details.sah_wrap_id ?? null,
          sah_item_id: details.sah_item_id ?? null,
          sah_detail_description: details.sah_detail_description ?? null,
          sah_hpt_id: details.sah_hpt_id ?? null,
          sah_hpt_description: details.sah_hpt_description ?? null,
          is_first_payment_item: details.is_first_payment_item ?? null,
          is_prescribed_item: details.is_prescribed_item ?? null,
          purchase_method: details.purchase_method ?? null,
          sah_wrap_unit_type: details.sah_wrap_unit_type ?? null,
        };
      },
    },
    {
      id: 'service_type_id',
      header: 'Service Type',
      type: 'text',
      deriveValue: (row) => {
        const serviceType = serviceTypes.find((s) => s.id === row.service_type_id);
        return serviceType ? serviceType.name : '-';
      },
      isEditable: false,
    },
    {
      id: 'claimed_total',
      header: 'Amount',
      type: 'text',
      classNames: {
        header: 'cell-align-right',
        cell: 'cell-align-right',
      },
      deriveValue: (row) => {
        const total = Number(row.units) * Number(row.unit_amount);
        return total > 0 ? `$${total.toFixed(2)}` : '';
      },
      isEditable: false,
    },
    {
      id: 'client_budget_id',
      header: 'Budget',
      type: 'custom',
      classNames: {
        header: 'cell-align-center',
        cell: 'cell-align-center',
      },
      style: { width: '50px' },
      component: BudgetIcon,
      isEditable: false,
    },
    {
      id: 'is_valid',
      header: 'Valid',
      type: 'custom',
      classNames: isValidating
        ? { column: 'cell-align-center bg-secondary bg-opacity-25 text-muted' }
        : { column: 'cell-align-center' },
      component: ClaimValidIcon,
      componentProps: (row: Row) => ({
        id: row.id,
        row,
        isValidating,
      }),
      isEditable: false,
    },
    {
      id: 'sa_status',
      header: 'SA Status',
      type: 'custom',
      // `showColumn` (not a conditional splice into the `columns` array) so the column definition
      // - header, width, component - stays in one place regardless of `hasApiPush`, matching how
      // TableView is meant to hide columns; a non-API claim's items table is otherwise unaffected
      // (AC #8: byte-identical UI for non-API providers).
      showColumn: Boolean(hasApiPush),
      classNames: { column: 'cell-align-center' },
      component: ClaimSahPushIcon,
      componentProps: (row: Row) => ({ id: row.id, row }),
      isEditable: false,
    },
    {
      id: 'error_message',
      header: 'Error',
      type: 'text',
      deriveValue: (row) => {
        const alertItem = row.alert_items?.[0];
        if (!alertItem) return '';
        const message = alertItem.alert?.message ?? '';
        return alertItem.detail ? `${message} (${alertItem.detail})` : message;
      },
      classNames: isValidating ? { column: 'bg-secondary bg-opacity-25 text-muted' } : undefined,
      showColumn: !isFinalised,
      isEditable: false,
    },
    {
      id: 'error_link',
      header: 'Investigate',
      type: 'text',
      showColumn: !isFinalised,
      deriveValue: (row) => (deriveAlertLink(row) ? 'View' : null),
      renderReadOnly: linkCellRender(
        (row) => {
          const link = deriveAlertLink(row);
          if (link) window.open(link, '_blank', 'noopener,noreferrer');
        },
        faEdit,
        {
          classNames: {
            cell: 'px-1',
          },
        } as any,
      ),
      isEditable: false,
    },
    {
      id: 'actions',
      header: '',
      type: 'custom',
      classNames: {
        cell: 'border-0',
      },
      component: ClaimItemActions,
      componentProps: (row: Row) => ({
        id: row.id,
        isIgnored: row.is_ignored,
        onIgnore,
        isLocked: !!row.statement?.id,
      }),
    },
  ];

  return columns;
};
